import type { MathfieldElement } from 'mathlive';
import { getFunctionsKeybindings } from 'zycommon-web/utils/symbolic-tools/functions/functions-math-live';
import { filterKeyBindings } from 'zycommon-web/utils/symbolic-tools/math-live-keyboard-shared';
import {
    getNumberAndUnitKeybindings,
    getNumberAndUnitMacros,
} from 'zycommon-web/utils/symbolic-tools/number-and-units/number-and-units-math-live';
import { getNumericVariablesKeybindings } from 'zycommon-web/utils/symbolic-tools/numeric-variables/numeric-variables-math-live';
import {
    getPropositionalLogicKeybindings,
    getPropositionalLogicMacros,
} from 'zycommon-web/utils/symbolic-tools/propositional-logic/propositional-logic-math-live';
import { getSymbolicNumberKeybindings } from 'zycommon-web/utils/symbolic-tools/symbolic-number/symbolic-number-math-live';

import type { BuilderPropertyRecord } from '../tool-settings';
import { getMathVirtualKeyboardLayoutForShortAnswer } from './mathlive-keyboard';
import { builderPropsToKeyboardContext, resolveNotation, resolveRequireUnits, type EditorAtomicSymbol } from './mathlive-question-context';

type MathfieldKeybinding = MathfieldElement['keybindings'][number];

/**
 * Same branch order as `customKeybindings` in zb-math-live.js.
 */
export function getCustomKeybindingsForShortAnswer(
    builder: BuilderPropertyRecord,
    editorAtomicSymbols: EditorAtomicSymbol[] = [],
): MathfieldKeybinding[] {
    const notation = resolveNotation(builder);
    const requireUnits = resolveRequireUnits(builder);
    const question = builderPropsToKeyboardContext(builder);
    const keybindings: MathfieldKeybinding[] = [];

    if (notation === 'functions') {
        keybindings.push(...(getFunctionsKeybindings() as MathfieldKeybinding[]));
    } else if (notation === 'number and units' || requireUnits) {
        keybindings.push(...(getNumberAndUnitKeybindings(question) as MathfieldKeybinding[]));
    } else if (notation === 'numeric variables') {
        keybindings.push(...(getNumericVariablesKeybindings() as MathfieldKeybinding[]));
    } else if (notation === 'propositional logic') {
        keybindings.push(...(getPropositionalLogicKeybindings(question, editorAtomicSymbols) as MathfieldKeybinding[]));
    } else {
        keybindings.push(...(getSymbolicNumberKeybindings(question) as MathfieldKeybinding[]));
    }

    return keybindings;
}

export function applyMathfieldMacros(
    mathFieldElement: MathfieldElement,
    builder: BuilderPropertyRecord,
    editorAtomicSymbols: EditorAtomicSymbol[] = [],
): void {
    const notation = resolveNotation(builder);
    const requireUnits = resolveRequireUnits(builder);

    if (notation === 'number and units' || requireUnits) {
        mathFieldElement.macros = getNumberAndUnitMacros(mathFieldElement.macros) as MathfieldElement['macros'];
    } else if (notation === 'propositional logic') {
        mathFieldElement.macros = getPropositionalLogicMacros(mathFieldElement.macros, editorAtomicSymbols) as MathfieldElement['macros'];
    }
}

export function applyMathfieldKeybindings(
    mathFieldElement: MathfieldElement,
    builder: BuilderPropertyRecord,
    originalKeybindings: readonly MathfieldKeybinding[],
    editorAtomicSymbols: EditorAtomicSymbol[] = [],
): void {
    const notation = resolveNotation(builder);
    const filtered = filterKeyBindings([...originalKeybindings], notation) as MathfieldKeybinding[];
    const custom = getCustomKeybindingsForShortAnswer(builder, editorAtomicSymbols);
    mathFieldElement.keybindings = filtered.concat(custom);
}

export function updateMathfieldKeyboardAndKeybindings(
    mathFieldElement: MathfieldElement,
    builder: BuilderPropertyRecord,
    {
        expectedString = '',
        editorAtomicSymbols = [],
        originalKeybindings,
    }: {
        expectedString?: string;
        editorAtomicSymbols?: EditorAtomicSymbol[];
        originalKeybindings: readonly MathfieldKeybinding[];
    },
): void {
    const vk = typeof window !== 'undefined' ? window.mathVirtualKeyboard : undefined;
    if (vk) {
        vk.layouts = getMathVirtualKeyboardLayoutForShortAnswer(builder, {
            expectedString,
            editorAtomicSymbols,
        }) as never;
    }
    applyMathfieldKeybindings(mathFieldElement, builder, originalKeybindings, editorAtomicSymbols);
}
